import React from "react";
import { Search, X } from "lucide-react";
import { Button } from "../../../../components/button";
import { DatePickerV3 } from "../../../../components/date-picker-v3";
import { Input } from "../../../../components/input";
import { SelectV2 } from "../../../../components/select-v2";

const TIME_FILTER_OPTIONS = [
  { value: "All time", label: "All time" },
  { value: "Today", label: "Today" },
  { value: "This Week", label: "This Week" },
  { value: "This Month", label: "This Month" },
  { value: "Last Year", label: "Last Year" },
  { value: "Custom Range", label: "Custom Range" },
];

const SORT_FILTER_OPTIONS = [
  { value: "newest_first", label: "Newest First" },
  { value: "oldest_first", label: "Oldest First" },
  { value: "least_points", label: "Points: Low to High" },
  { value: "most_points", label: "Most Points" },
  { value: "most_responses", label: "Most Responses" },
];

const getCalendarDateTimestamp = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const isDateAfter = (firstDate: Date, secondDate: Date) =>
  getCalendarDateTimestamp(firstDate) > getCalendarDateTimestamp(secondDate);

const getNextCalendarDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);

const getPreviousCalendarDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);

export type SurveyPageFilterState = {
  search: string;
  timeFilter: string;
  sortFilter: string;
  startDate?: Date;
  endDate?: Date;
};

export type SurveyPageFilterHandlers = {
  onSearchChange: (value: string) => void;
  onTimeFilterChange: (value: string) => void;
  onSortFilterChange: (value: string) => void;
  onStartDateChange: (value: Date | undefined) => void;
  onEndDateChange: (value: Date | undefined) => void;
  onClearDateRange: () => void;
};

type SurveyPageFiltersProps = {
  t: (key: string) => string;
  filters: SurveyPageFilterState;
  handleChange: SurveyPageFilterHandlers;
};

const SurveyPageFilters: React.FC<SurveyPageFiltersProps> = ({ t, filters, handleChange }) => {
  return (
    <div className="mb-5 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="w-full sm:max-w-md sm:flex-1">
          <div className="relative">
            <Input
              startIcon={<Search className="h-4 w-4 text-gray-400" />}
              type="text"
              placeholder="Search by survey title..."
              value={filters.search}
              onChange={(e) => handleChange.onSearchChange(e.target.value)}
              className="h-10 pr-9"
            />
            {filters.search ? (
              <button
                type="button"
                onClick={() => handleChange.onSearchChange("")}
                className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                aria-label="Clear survey search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        {filters.timeFilter !== "Custom Range" ? (
          <SelectV2
            value={filters.timeFilter}
            onChange={handleChange.onTimeFilterChange}
            options={TIME_FILTER_OPTIONS}
            ariaLabel="Survey time filter"
            className="w-full sm:w-40"
            triggerClassName="h-10 min-h-10"
          />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <DatePickerV3
              value={filters.startDate}
              onChange={(date) => {
                if (date && filters.endDate && isDateAfter(date, filters.endDate)) {
                  return;
                }

                handleChange.onStartDateChange(date);
              }}
              placeholder="Start date"
              primaryColor="#10b981"
              ariaLabel="Start date"
              className="h-10 w-full sm:w-40"
              disableFromDate={
                filters.endDate ? getNextCalendarDate(filters.endDate) : undefined
              }
            />
            <span className="text-sm text-gray-500">to</span>
            <DatePickerV3
              value={filters.endDate}
              onChange={(date) => {
                if (date && filters.startDate && isDateAfter(filters.startDate, date)) {
                  return;
                }

                handleChange.onEndDateChange(date);
              }}
              placeholder="End date"
              primaryColor="#10b981"
              ariaLabel="End date"
              className="h-10 w-full sm:w-40"
              disableToDate={
                filters.startDate ? getPreviousCalendarDate(filters.startDate) : undefined
              }
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleChange.onClearDateRange}
              className="h-10 w-10 p-2 text-gray-500 hover:text-gray-700"
              aria-label="Clear custom date range"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <SelectV2
          value={filters.sortFilter}
          onChange={handleChange.onSortFilterChange}
          options={SORT_FILTER_OPTIONS}
          ariaLabel="Survey sort order"
          className="w-full sm:w-48"
          triggerClassName="h-10 min-h-10"
        />
        </div>
      </div>
    </div>
  );
};

export default SurveyPageFilters;
